import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Card, Stack, Typography } from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PremiumHeader from "./components/PremiumHeader";
import FilterPanel from "./components/FilterPanel";
import TimetableGrid from "./components/TimetableGrid";
import { reportsApi } from "../shared/api";
import { defaultFilter, FilterState, Options, StaffBlock, StaffIndexItem } from "./types";
import StepUpSpinner from "../../../Components/Loader/StepUpSpinner";

function toParams(f: FilterState) {
  return {
    week_start: f.week_start || undefined,
    date_from: f.date_from || undefined,
    date_to: f.date_to || undefined,
    staff_ids: f.staff_ids.length ? f.staff_ids : undefined,
    location_ids: f.location_ids.length ? f.location_ids : undefined,
    study_modes: f.study_modes.length ? f.study_modes : undefined,
    grade_ids: f.grade_ids.length ? f.grade_ids : undefined,
    subject_ids: f.subject_ids.length ? f.subject_ids : undefined,
    program_types: f.program_types.length ? f.program_types : undefined,
    class_types: f.class_types.length ? f.class_types : undefined,
    include_zero_student: f.include_zero_student ? 1 : undefined,
    include_one_off: f.include_one_off ? 1 : undefined,
    include_inactive_staff: f.include_inactive_staff ? 1 : undefined,
    staff_search: f.staff_search || undefined,
    payment_type: f.payment_type.length ? f.payment_type : undefined,
  };
}

export default function StaffTimetableReportPage() {
  const [filter, setFilter] = useState<FilterState>(defaultFilter());
  const [staffList, setStaffList] = useState<StaffIndexItem[]>([]);
  const [activeDetail, setActiveDetail] = useState<StaffBlock | null>(null);
  const [current, setCurrent] = useState(0);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<null | "csv" | "pdf">(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  const [options, setOptions] = useState<Options>({
    grades: [], subjects: [], staff: [], locations: [],
    study_modes: [], program_types: [], class_types: [],
  });

  const indexReqRef = useRef(0);
  const detailReqRef = useRef(0);
  const params = useMemo(() => toParams(filter), [filter]);
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    let alive = true;
    reportsApi.staffTimetableOptions()
      .then((res) => { if (alive) setOptions(res as Options); })
      .catch((e: any) => { if (alive) setError(e.message || String(e)); });
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    const token = ++indexReqRef.current;
    setLoading(true); setError(null);
    try {
      const res = await reportsApi.staffTimetableIndex(params);
      if (token !== indexReqRef.current) return;
      setStaffList(res.data);
      setSummary(res.summary as Record<string, number>);
      setRange({ from: res.range.from, to: res.range.to });
      setCurrent(0);
      setActiveDetail(null);
      setRefreshedAt(new Date());
    } catch (e: any) {
      if (token === indexReqRef.current) setError(e.message || String(e));
    } finally {
      if (token === indexReqRef.current) { setLoading(false); setFirstLoad(false); }
    }
  }, [paramsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Fetch detail for the currently-selected staff whenever it changes.
  const activeIndexItem = staffList[current];
  const activeStaffId = activeIndexItem?.staff_id ?? null;
  const activeKey = `${paramsKey}|${activeStaffId ?? "null"}|${current}`;

  useEffect(() => {
    if (!activeIndexItem) { setActiveDetail(null); return; }
    const token = ++detailReqRef.current;
    setDetailLoading(true);
    reportsApi.staffTimetablePreview({ ...params, staff_id: activeStaffId ?? undefined })
      .then((res) => {
        if (token !== detailReqRef.current) return;
        const block = (res.staff && res.staff[0]) ? (res.staff[0] as unknown as StaffBlock) : null;
        setActiveDetail(block);
      })
      .catch((e: any) => {
        if (token === detailReqRef.current) setError(e.message || String(e));
      })
      .finally(() => {
        if (token === detailReqRef.current) setDetailLoading(false);
      });
  }, [activeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const doExport = async (kind: "csv" | "pdf") => {
    setExporting(kind); setError(null);
    try {
      const fn = kind === "csv" ? reportsApi.staffTimetableCsv : reportsApi.staffTimetablePdf;
      await fn(params);
    } catch (e: any) {
      setError(`Export failed: ${e.message || e}`);
    } finally {
      setExporting(null);
    }
  };

  const doPdf = async (variant: "detailed" | "compact", opts: { includeIds: boolean }) => {
    setExporting("pdf"); setError(null);
    try {
      if (variant === "compact") {
        await reportsApi.staffTimetablePdfCompact({
          ...params,
          include_ids: opts.includeIds ? 1 : undefined,
        });
      } else {
        await reportsApi.staffTimetablePdf(params);
      }
    } catch (e: any) {
      setError(`Export failed: ${e.message || e}`);
    } finally {
      setExporting(null);
    }
  };

  const totalStaff = staffList.length;

  return (
    <Box p={2}>
      <PremiumHeader
        refreshedAt={refreshedAt}
        loading={loading}
        exporting={exporting}
        onRefresh={load}
        onCsv={() => doExport("csv")}
        onPdf={doPdf}
      />

      <Card variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, background: "#f8fafc" }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Typography variant="body2"><b>{summary.total_staff ?? 0}</b> staff</Typography>
          <Typography variant="body2"><b>{summary.total_classes ?? 0}</b> classes</Typography>
          <Typography variant="body2"><b>{summary.total_students ?? 0}</b> students</Typography>
          <Typography variant="body2"><b>{summary.total_hours ?? 0}</b> hrs</Typography>
          <Typography variant="body2">Online: <b>{summary.online_classes ?? 0}</b></Typography>
          <Typography variant="body2">In-Person: <b>{summary.in_person_classes ?? 0}</b></Typography>
          <Typography variant="body2">Hybrid: <b>{summary.hybrid_classes ?? 0}</b></Typography>
          <Typography variant="body2">Zero-student: <b>{summary.zero_student_classes ?? 0}</b></Typography>
          {range && (
            <Typography variant="body2" color="text.secondary">
              Week: {range.from} — {range.to}
            </Typography>
          )}
        </Stack>
      </Card>

      <FilterPanel
        value={filter}
        onChange={setFilter}
        onClear={() => setFilter(defaultFilter())}
        loading={loading}
        options={options}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {firstLoad && loading ? (
        <Card variant="outlined" sx={{ py: 8, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <StepUpSpinner size="large" label="Loading staff timetables" />
        </Card>
      ) : totalStaff === 0 ? (
        <Card variant="outlined" sx={{ py: 6, textAlign: "center" }}>
          <Typography variant="subtitle1" fontWeight={600}>No staff/classes match the current filters.</Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Try changing the week, locations, or turning on “Include zero-student classes”.
          </Typography>
          <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setFilter(defaultFilter())}>Clear Filters</Button>
        </Card>
      ) : (
        <>
          <Card variant="outlined" sx={{ p: 1, mb: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" variant="outlined" startIcon={<NavigateBeforeIcon />}
                onClick={() => setCurrent((c) => Math.max(0, c - 1))}
                disabled={current <= 0}>
                Previous
              </Button>
              <Typography variant="body2" fontWeight={600} sx={{ flex: 1, textAlign: "center" }}>
                {activeIndexItem?.staff_name} — {current + 1} of {totalStaff}
              </Typography>
              <Button size="small" variant="outlined" endIcon={<NavigateNextIcon />}
                onClick={() => setCurrent((c) => Math.min(totalStaff - 1, c + 1))}
                disabled={current >= totalStaff - 1}>
                Next
              </Button>
            </Stack>
          </Card>

          {detailLoading && !activeDetail ? (
            <Card variant="outlined" sx={{ py: 8, display: "flex", justifyContent: "center" }}>
              <StepUpSpinner size="large" label={`Loading ${activeIndexItem?.staff_name ?? "staff"}`} />
            </Card>
          ) : activeDetail ? (
            <TimetableGrid staff={activeDetail} />
          ) : null}
        </>
      )}
    </Box>
  );
}
